import { useState, useEffect } from 'react'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { Loader2 } from 'lucide-react'
import { trpc } from '../providers/trpc'
import { toast } from 'sonner'

interface PasswordResetProps {
  isOpen: boolean
  onClose: () => void
}

export default function PasswordReset({ isOpen, onClose }: PasswordResetProps) {
  const [step, setStep] = useState<'email' | 'sent'>('email')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const requestReset = trpc.passwordReset.requestReset.useMutation({
    onSuccess: () => {
      setStep('sent')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to send reset link. Please try again.')
    },
  })

  const trapRef = useFocusTrap(isOpen, { onEscape: onClose, returnFocusOnDeactivate: true })

  if (!isOpen) return null

  const validate = () => {
    const e: Record<string, string> = {}
    if (!email) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    requestReset.mutate({ email: email.trim().toLowerCase() })
  }

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-reset-title"
        className="relative w-full max-w-sm rounded-2xl p-8"
        style={{ background: '#07110D', border: '1px solid rgba(247,248,244,0.12)' }}
      >
        <button
          onClick={onClose}
          aria-label="Close password reset dialog"
          className="absolute right-4 top-4 h-8 w-8 flex items-center justify-center rounded-lg transition-colors"
          style={{ color: '#A8B8AE' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(247,248,244,0.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          ✕
        </button>
        {step === 'email' ? (
          <div className="space-y-6">
            <div>
              <h2 id="password-reset-title" className="font-display text-xl font-semibold" style={{ color: '#F7F8F4' }}>Reset Password</h2>
              <p className="font-body mt-2 text-sm" style={{ color: '#A8B8AE' }}>
                Enter your email and we&apos;ll send you a link to reset your password.
              </p>
            </div>
            <div>
              <label className="label-accent mb-2 block" style={{ color: '#A8B8AE' }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((prev) => { const n = { ...prev }; delete n.email; return n; }); }}
                placeholder="you@example.com"
                className="w-full rounded-xl border px-5 py-3 font-body text-sm outline-none transition-colors focus:border-white"
                style={{ borderColor: errors.email ? '#FF445F' : 'rgba(247,248,244,0.12)', background: '#1A2332', color: '#F7F8F4' }}
                aria-label="Email address"
                aria-invalid={!!errors.email}
              />
              {errors.email && <p className="font-body mt-1 text-xs" style={{ color: '#FF445F' }}>{errors.email}</p>}
            </div>
            <button
              onClick={handleSubmit}
              disabled={requestReset.isPending}
              className="w-full rounded-xl py-3 text-sm font-medium transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: '#F7F8F4', color: '#07110D' }}
            >
              {requestReset.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {requestReset.isPending ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
        ) : (
          <div className="text-center space-y-4 pt-2">
            <div className="text-4xl">📧</div>
            <h2 className="font-display text-xl font-semibold" style={{ color: '#F7F8F4' }}>Check your email</h2>
            <p className="font-body text-sm" style={{ color: '#A8B8AE' }}>
              If an account exists for <strong style={{ color: '#F7F8F4' }}>{email}</strong>, you&apos;ll receive a password reset link within a few minutes.
            </p>
            <button
              onClick={onClose}
              className="w-full rounded-xl py-3 text-sm font-medium transition-all hover:opacity-90"
              style={{ background: 'rgba(247,248,244,0.08)', color: '#F7F8F4' }}
            >
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
